import { HttpStatusCode } from 'axios'

import { ApiResponseSchema } from '@/shared/schemas/api-schema'

import { ApiError } from '@shared/api/api-factory'

import type { ZodType, ZodTypeDef } from 'zod'
import type { TApiResponseWithRequiredData } from '@/shared/schemas/api-schema'

export function parseApiResponse<T>(
  responseData: unknown,
  dataSchema: ZodType<T, ZodTypeDef, unknown>
): TApiResponseWithRequiredData<T> {
  const isApiResponseFormat =
    typeof responseData === 'object' &&
    responseData !== null &&
    'statusCode' in responseData &&
    'data' in responseData

  if (!isApiResponseFormat) {
    const { success, error, data } = dataSchema.safeParse(responseData)
    if (!success) {
      console.error('Direct Data Schema Error:', error)
      throw new ApiError({
        errorCode: 'PARSE_ERROR',
        message: 'Invalid response data format',
        statusCode: HttpStatusCode.InternalServerError,
      })
    }

    return {
      errorCode: null,
      message: 'Success',
      data: data as NonNullable<T>,
      statusCode: HttpStatusCode.Ok,
    }
  }

  const {
    data: apiResponseData,
    error: apiResponseError,
    success: isApiResponseSuccess,
  } = ApiResponseSchema.safeParse(responseData)
  if (!isApiResponseSuccess) {
    console.error('API Response Schema Error:', apiResponseError)
    throw new ApiError({
      errorCode: 'PARSE_ERROR',
      message: 'Invalid API response format',
      statusCode: HttpStatusCode.InternalServerError,
    })
  }

  if (apiResponseData.statusCode >= HttpStatusCode.BadRequest) {
    throw new ApiError({
      message: apiResponseData.message,
      statusCode: apiResponseData.statusCode,
    })
  }

  const { success, error, data } = dataSchema.safeParse(apiResponseData.data)
  if (!success || !data) {
    console.error('Data Schema Error:', error)
    console.error('Schema validation details:', error?.issues)
    console.error('Failed to parse data:', apiResponseData.data)
    throw new ApiError({
      errorCode: 'PARSE_ERROR',
      message: 'Invalid response data format',
      statusCode: HttpStatusCode.InternalServerError,
    })
  }

  return { ...apiResponseData, data }
}
